import useEmblaCarousel from 'embla-carousel-react';
import React, { useMemo } from 'react';
import { GoalTemplateCard } from 'shared/components/ui/goal-template-card';
import { Section } from 'shared/components/ui/section';
import { useShowGoalTemplatePreview } from 'shared/contexts/goal-template-preview';
import { GoalTemplate } from 'shared/types/goal-template';
import { ID } from 'shared/types/id';
import { chunkArray } from 'shared/utils/chunk-array';
import { useTheme } from 'styled-components';

import * as Styled from './category-overview.style';

type ListProps = {
  templates: GoalTemplate[];
  onTemplate: (id: ID) => void;
};

const DesktopList: React.FC<ListProps> = ({ templates, onTemplate }) => (
  <Styled.List>
    {templates.map((template) => (
      <Styled.ListItem key={template.id}>
        <GoalTemplateCard template={template} onClick={onTemplate} />
      </Styled.ListItem>
    ))}
  </Styled.List>
);

const MobileList: React.FC<ListProps> = ({ templates, onTemplate }) => {
  const [emblaRef] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
  });

  const chunkedTemplates = useMemo(() => chunkArray(templates, 3), [templates]);

  return (
    <Styled.EmblaContainer ref={emblaRef}>
      <Styled.EmblaList>
        {chunkedTemplates.map((templatesChunk, index) => (
          <Styled.EmblaListItem key={index}>
            <Styled.EmblaSubList>
              {templatesChunk.map((template) => (
                <Styled.ListItem key={template.id}>
                  <GoalTemplateCard template={template} onClick={onTemplate} />
                </Styled.ListItem>
              ))}
            </Styled.EmblaSubList>
          </Styled.EmblaListItem>
        ))}
      </Styled.EmblaList>
    </Styled.EmblaContainer>
  );
};

export type CategoryOverviewProps = {
  title: string;
  templates: GoalTemplate[];
};

export const CategoryOverview: React.FC<CategoryOverviewProps> = ({
  title,
  templates,
}) => {
  const theme = useTheme();
  const openPreview = useShowGoalTemplatePreview();

  const showPreview = (id: ID) => {
    const template = templates.find((template) => template.id === id);

    if (!template) {
      return;
    }

    openPreview(template);
  };

  return (
    <Section title={title} canCollapse initialOpen>
      <Styled.ListContainer>
        {theme.isMobile ? (
          <MobileList templates={templates} onTemplate={showPreview} />
        ) : (
          <DesktopList templates={templates} onTemplate={showPreview} />
        )}
      </Styled.ListContainer>
    </Section>
  );
};
